import { Injectable, inject, NgZone } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of, firstValueFrom } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AppNotification, UnreadCountResponse } from '../models/notification.models';

interface SseTicketResponse {
  ticket: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private http = inject(HttpClient);
  private ngZone = inject(NgZone);
  
  private readonly API_URL = `${environment.apiUrl}/notifications`;
  private eventSource?: EventSource;
  private isConnecting = false;
  private reconnectAttempts = 0;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  
  private notificationsSubject = new BehaviorSubject<AppNotification[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();
  
  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();
  
  private isConnectedSubject = new BehaviorSubject<boolean>(false);
  public isConnected$ = this.isConnectedSubject.asObservable();

  async connectToNotificationStream(): Promise<void> {
    // Гард стоїть ПЕРЕД close(): раніше паралельний виклик закривав живий стрім і виходив,
    // не відкривши новий — вкладка лишалась без сповіщень до перезавантаження сторінки.
    if (this.isConnecting) {
      return;
    }

    if (this.eventSource) {
      this.eventSource.close();
    }

    this.isConnecting = true;

    try {
      // 1. Отримуємо одноразовий ticket
      const { ticket } = await firstValueFrom(
        this.http.post<SseTicketResponse>(`${this.API_URL}/sse-ticket`, {})
      );

      // 2. Підключаємося до SSE з ticket
      const streamUrl = `${this.API_URL}/stream?ticket=${ticket}`;

      this.eventSource = new EventSource(streamUrl, {
        withCredentials: true
      });

      this.eventSource.onopen = () => {
        this.ngZone.run(() => {
          this.reconnectAttempts = 0;
          this.isConnectedSubject.next(true);
        });
      };

      this.eventSource.addEventListener('notification', (event: MessageEvent) => {
        this.ngZone.run(() => this.handleIncoming(event));
      });

      this.eventSource.onmessage = (event: MessageEvent) => {
        this.ngZone.run(() => this.handleIncoming(event));
      };

      this.eventSource.onerror = () => {
        this.ngZone.run(() => {
          this.isConnectedSubject.next(false);
          this.eventSource?.close();
          this.eventSource = undefined;
          this.scheduleReconnect();
        });
      };

      this.isConnecting = false;
    } catch (error) {
      this.isConnecting = false;
      // 401 означає, що сеанс скінчився — authErrorInterceptor уже веде на /login.
      // Ретраїти немає сенсу: таймер лише крутився б на сторінці логіну.
      if (error instanceof HttpErrorResponse && error.status === 401) {
        return;
      }
      console.error('Failed to get SSE ticket:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Експоненційний відступ: 1с, 2с, 4с … до 30с. Глухі 5 секунд означали, що будь-який
   * системний збій перетворювався на рівний флуд по 12 підключень за хвилину з кожної вкладки.
   */
  private scheduleReconnect(): void {
    const delay = Math.min(30_000, 1000 * 2 ** this.reconnectAttempts);
    this.reconnectAttempts += 1;
    console.warn(`SSE disconnected, reconnecting in ${delay}ms`);
    this.reconnectTimer = setTimeout(() => this.connectToNotificationStream(), delay);
  }

  /** Подія приходить або як named-event, або як onmessage — обробник спільний. */
  private handleIncoming(event: MessageEvent): void {
    try {
      const notification: AppNotification = JSON.parse(event.data);
      const current = this.notificationsSubject.value;
      if (current.some(n => n.id === notification.id)) {
        return;
      }
      this.notificationsSubject.next([notification, ...current]);
      if (!notification.isRead) {
        this.unreadCountSubject.next(this.unreadCountSubject.value + 1);
      }
      this.showBrowserNotification(notification);
    } catch (error) {
      console.error('Error parsing SSE message:', error);
    }
  }

  loadNotifications(unreadOnly: boolean = false): Observable<AppNotification[]> {
    const params = new HttpParams().set('unreadOnly', unreadOnly.toString());
    
    return this.http.get<AppNotification[]>(this.API_URL, { params }).pipe(
      tap(notifications => {
        this.notificationsSubject.next(notifications);
      }),
      catchError(error => {
        console.error('Error loading notifications:', error);
        return of([]);
      })
    );
  }

  markAsRead(id: string): Observable<AppNotification> {
    return this.http.put<AppNotification>(`${this.API_URL}/${id}/read`, {}).pipe(
      tap(updated => {
        const current = this.notificationsSubject.value;
        const index = current.findIndex(n => n.id === id);
        if (index !== -1) {
          current[index] = updated;
          this.notificationsSubject.next([...current]);
          this.updateUnreadCount();
        }
      }),
      catchError(error => {
        console.error('Error marking notification as read:', error);
        throw error;
      })
    );
  }

  markAllAsRead(): Observable<void> {
    return this.http.put<void>(`${this.API_URL}/read-all`, {}).pipe(
      tap(() => {
        const current = this.notificationsSubject.value.map(n => ({
          ...n,
          isRead: true
        }));
        this.notificationsSubject.next(current);
        this.unreadCountSubject.next(0);
      }),
      catchError(error => {
        console.error('Error marking all notifications as read:', error);
        throw error;
      })
    );
  }

  getUnreadCount(): Observable<UnreadCountResponse> {
    return this.http.get<UnreadCountResponse>(`${this.API_URL}/unread-count`).pipe(
      tap(result => {
        this.unreadCountSubject.next(result.count);
      }),
      catchError(error => {
        console.error('Error getting unread count:', error);
        return of({ count: 0 });
      })
    );
  }

  private updateUnreadCount(): void {
    this.getUnreadCount().subscribe();
  }

  private showBrowserNotification(notification: AppNotification): void {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/assets/logo.png',
        badge: '/assets/badge.png'
      });
    }
  }

  requestNotificationPermission(): void {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }

  disconnect(): void {
    // Скасувати відкладений реконект: інакше таймер спрацює вже після ngOnDestroy
    // і підніме стрім у знищеному компоненті.
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    this.reconnectAttempts = 0;

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = undefined;
      this.isConnectedSubject.next(false);
    }
  }
}
